import _, {compact, findLast} from 'lodash';
import {createSelector} from 'reselect';

import {onlyItem} from '../../../../libs/shared/helpers/src/utilities/arrayHelpers';
import {VisualSearchActivityModel} from '../../../components/inboxes/cells/content/cell/cellSearchActivitySummaryHighlight';
import {isTeammateActive} from '../../../helpers/modelHelpers';
import {
  ConversationDateGetter,
  findConversationsReminder,
  findConversationStatus,
  findUnreadStatus,
  makeConversationDateGetter,
  UnreadStatusesEnum,
} from '../../../helpers/models/conversationHelpers';
import {isTeamNamespace} from '../../../helpers/models/namespaceHelpers';
import {tagHasBehaviors} from '../../../helpers/models/tagHelpers';
import {AppParametricSelector, wrapInShallowSelector} from '../../../helpers/reselectHelpers';
import {ActivityModel, ActivityVisualTypesEnum} from '../../../models/api/activityModel';
import {ActivitySourceVisualTypesEnum} from '../../../models/api/activitySourceModel';
import {CommentModel, CommentStatusesEnum, DraftCommentModel} from '../../../models/api/commentModel';
import {ConversationSortPropertiesEnum} from '../../../models/api/conversationCommonTypes';
import {SearchActivityModel} from '../../../models/api/conversationHighlightModel';
import {ConversationTrackerStagesEnum} from '../../../models/api/conversationTrackerModel';
import {DraftSeedModel} from '../../../models/api/draftSeedModel';
import {DraftSummaryModel} from '../../../models/api/draftSummaryModel';
import {PrecogModel, PrecogStatusTypesEnum, PrecogTypesEnum} from '../../../models/api/precogModel';
import {TagBehaviorsEnum} from '../../../models/api/tagModel';
import {TeammateModel} from '../../../models/api/teammateModel';
import {TeammatesFoldersEnum} from '../../../models/url/inboxesUrlModel';
import {CellsContext, CellsContextTypesEnum} from '../../panels/cells/context/cellsContext';
import {
  InboxesConversationsParameters,
  InboxesParametersTypesEnum,
} from '../../panels/inboxes/parameters/inboxesParameters';
import {getActivities, getComments, getMessages} from '../dataSelectors';
import {OptionalMessageSelector} from '../messages/messagesSelectors';
import {getTags} from '../tags/tagsSelectors';
import {getFindTeammate} from '../teammates/lazyTeammatesSelectors';
import {getFlatTeams} from '../teams/teamsSelectors';
import {makeGetPrecogs, PrecogsSelector} from './conversationsPrecogsSelectors';
import {
  ConversationBaseSelector,
  ConversationSelector,
  makeGetConversationInboxes,
  OptionalConversationSelector,
} from './conversationsSelectors';

type OptionalParametersSelector<P> = AppParametricSelector<P, InboxesConversationsParameters | undefined>;
type CellsContextSelector<P> = AppParametricSelector<P, CellsContext>;
type TeammateIdSelector<P> = AppParametricSelector<P, number>;
type CommentsSelector<P> = AppParametricSelector<P, ReadonlyArray<CommentModel>>;
type OptionalActivitySelector<P> = AppParametricSelector<P, ActivityModel | undefined>;
type UnreadStatusSelector<P> = AppParametricSelector<P, UnreadStatusesEnum>;

export interface LastCommentModel {
  author: TeammateModel;
  comment: CommentModel;
}

export interface LastCommentDraftModel {
  author: TeammateModel;
  comment: DraftCommentModel;
}

export interface CommentDraftSummary {
  author: TeammateModel;
  summary: DraftSummaryModel;
}

export interface BumpCommentModel {
  comment: CommentModel;
  author: TeammateModel;
  isLastComment?: boolean;
}

/*
 * First line.
 */

export function makeGetDiscussionTrackerTeammates<P>(
  getConversation: ConversationBaseSelector<P>,
  getDelegatedTeammateId: TeammateIdSelector<P>,
) {
  const getDiscussionTrackers = createSelector(getConversation, (conversation) => {
    // We only need the trackers if we don't have a recipient.
    if (conversation.recipient) {
      return [];
    }

    // If so, filter to exclude inactive trackers.
    return _(conversation.trackers)
      .values()
      .compact()
      .filter((t) => t.stage !== ConversationTrackerStagesEnum.NONE)
      .value();
  });

  const getDiscussionTrackersShallow = wrapInShallowSelector(getDiscussionTrackers);
  return createSelector(
    [getDiscussionTrackersShallow, getDelegatedTeammateId, getFindTeammate],
    (trackers, delegatedTeammateId, findTeammate) => {
      // If the current teammate is the only tracker, display it.
      const onlyTracker = onlyItem(trackers);
      if (onlyTracker && onlyTracker.teammateId === delegatedTeammateId) {
        return compact([
          findTeammate(
            onlyTracker.teammateId,
            'conversationCellSelectors makeGetDiscussionTrackerTeammates onlyTracker',
          ),
        ]);
      }

      // Otherwise, filter it out.
      return _(trackers)
        .filter((t) => t.teammateId !== delegatedTeammateId)
        .map((t) => findTeammate(t.teammateId, 'conversationCellSelectors makeGetDiscussionTrackerTeammates'))
        .compact()
        .value();
    },
  );
}

export function makeGetDate<P>(
  getConversation: ConversationBaseSelector<P>,
  getDelegatedTeammateId: TeammateIdSelector<P>,
  getParameters: OptionalParametersSelector<P>,
  getCellsContext: CellsContextSelector<P>,
) {
  const getDateGetter = createSelector(
    [getDelegatedTeammateId, getParameters, getCellsContext],
    (delegatedTeammateId, parameters, cellsContext): ConversationDateGetter =>
      parameters
        ? makeConversationDateGetter(cellsContext, parameters)
        : (c) => {
            const tracker = c.trackers[delegatedTeammateId];
            if (tracker) {
              return tracker.bumpedAt;
            }

            return c.orders[ConversationSortPropertiesEnum.BUMPED_AT];
          },
  );

  return createSelector([getDateGetter, getConversation], (dateGetter, conversation) =>
    dateGetter(conversation),
  );
}

export function makeGetReminderDate<P>(
  getConversation: ConversationBaseSelector<P>,
  getCellsContext: CellsContextSelector<P>,
) {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) => {
    // Find the user's reminder for this conversation, if any.
    const reminder = findConversationsReminder(cellsContext, [conversation]);
    return reminder && reminder.remindAt;
  });
}

export function makeGetAssignee<P>(
  getConversation: ConversationBaseSelector<P>,
  getParameters: OptionalParametersSelector<P>,
) {
  return createSelector(
    [getConversation, getFindTeammate, getParameters],
    (conversation, findTeammate, parameters) => {
      if (
        parameters &&
        parameters.type === InboxesParametersTypesEnum.USER &&
        (parameters.folder === TeammatesFoldersEnum.ASSIGNED ||
          parameters.folder === TeammatesFoldersEnum.ASSIGNED_SHARED)
      ) {
        return undefined;
      }

      return (
        (conversation.assigneeId &&
          findTeammate(conversation.assigneeId, 'conversationCellSelectors makeGetAssignee')) ||
        undefined
      );
    },
  );
}

export function makeGetConversationInbox<P>(
  getConversation: ConversationBaseSelector<P>,
  getParameters: OptionalParametersSelector<P>,
  getCellsContext: CellsContextSelector<P>,
) {
  const getConversationInboxes = makeGetConversationInboxes(getConversation);
  return createSelector(
    [getConversationInboxes, getParameters, getCellsContext],
    (conversationInboxes, parameters, cellsContext) => {
      if (
        parameters &&
        (parameters.type === InboxesParametersTypesEnum.TEAMMATE_INBOX ||
          parameters.type === InboxesParametersTypesEnum.INBOX)
      ) {
        return undefined;
      }

      return _(conversationInboxes)
        .filter((i) => isTeamNamespace(i.namespace) || i.namespace === cellsContext.namespace)
        .first();
    },
  );
}

/*
 * Second line.
 */

export function makeGetConversationTags<P>(getConversation: ConversationBaseSelector<P>) {
  return createSelector([getConversation, getTags], (conversation, tags) =>
    _(conversation.tagIds)
      .map((id) => tags[id])
      .compact()
      .filter((t) => t.showInCells && !t.isMainInboxTag && tagHasBehaviors(t, TagBehaviorsEnum.CAN_DISPLAY))
      .value(),
  );
}

export function makeGetIsStarred<P>(
  getConversation: ConversationBaseSelector<P>,
  getDelegatedTeammateId: TeammateIdSelector<P>,
) {
  return createSelector(
    [getConversation, getDelegatedTeammateId, getFindTeammate],
    (conversation, delegatedTeammateId, findTeammate) => {
      const starredTagId = findTeammate(
        delegatedTeammateId,
        'conversationCellSelectors, makeGetIsStarred',
      )?.starredTagId;

      return Boolean(starredTagId && conversation.tagIds.includes(starredTagId));
    },
  );
}

/*
 * Third line.
 */

export function makeGetTeam<P>(
  getConversation: ConversationBaseSelector<P>,
  getCellsContext: CellsContextSelector<P>,
) {
  return createSelector(
    [getConversation, getCellsContext, getFlatTeams],
    (conversation, cellsContext, teams) => {
      // No need to show an indicator if there's only one team.
      if (teams.length < 2) {
        return undefined;
      }

      // No need to show an indicator if we're already in the team.
      if (cellsContext.type === CellsContextTypesEnum.TEAM) {
        return undefined;
      }

      // Otherwise, find the corresponding team.
      return teams.find((t) => t.namespace === conversation.namespace);
    },
  );
}

export function makeGetBumpActivity<P>(getConversation: ConversationSelector<P>) {
  return createSelector([getConversation, getActivities], (conversation, activities) => {
    const {bumpActivityId} = conversation;

    if (!bumpActivityId) {
      return undefined;
    }

    return activities[bumpActivityId];
  });
}

const optimisticallySentDraftStatuses = Object.freeze([
  CommentStatusesEnum.PENDING,
  CommentStatusesEnum.SENDING,
  CommentStatusesEnum.FAILED,
]);

function makeGetOptimisticallySentLastCommentDraft<P>(getDraftComments: CommentsSelector<P>) {
  return createSelector(getDraftComments, (draftComments) =>
    findLast(draftComments, (c) => optimisticallySentDraftStatuses.includes(c.status)),
  );
}

export function makeGetLastComment<P>(
  getConversation: OptionalConversationSelector<P>,
  getDraftComments: CommentsSelector<P>,
): OptionalCommentSelector<P> {
  return createSelector(
    [getConversation, makeGetOptimisticallySentLastCommentDraft(getDraftComments), getComments],
    (conversation, lastCommentDraft, comments) => {
      if (!conversation) {
        return undefined;
      }

      // If we have sending draft comments, we want to use the last one.
      if (lastCommentDraft) {
        return lastCommentDraft;
      }

      // Otherwise, return last comment of the conversation.
      return conversation.lastComment && comments[conversation.lastComment.uid];
    },
  );
}

function makeGetLastCommentAuthor<P>(getLastComment: AppParametricSelector<P, CommentModel | undefined>) {
  return createSelector(
    [getLastComment, getFindTeammate],
    (lastComment, findTeammate) =>
      lastComment && findTeammate(lastComment.authorId, 'conversationCellSelectors makeGetLastCommentAuthor'),
  );
}

type OptionalCommentSelector<P> = AppParametricSelector<P, CommentModel | undefined>;
export function makeGetLastCommentWithAuthor<P>(getLastComment: OptionalCommentSelector<P>) {
  return createSelector([getLastComment, makeGetLastCommentAuthor(getLastComment)], (comment, author) => {
    if (!comment || !author) {
      return undefined;
    }

    const result: LastCommentModel = {
      comment,
      author,
    };
    return result;
  });
}

export function makeGetBumpComment<P>(
  getConversation: ConversationSelector<P>,
  getBumpActivity: OptionalActivitySelector<P>,
  getDraftComments: CommentsSelector<P>,
) {
  const getLastComment = makeGetLastComment(getConversation, getDraftComments);
  return createSelector(
    [getBumpActivity, getComments, getFindTeammate, getLastComment],
    (bumpActivity, comments, findTeammate, lastComment) => {
      // If we don't have a bump activity, nothing to do
      if (!bumpActivity) {
        return undefined;
      }

      // If our bump activity isn't a comment, nothing to do.
      if (bumpActivity.visualType !== ActivityVisualTypesEnum.COMMENT) {
        return undefined;
      }

      // Grab the comment from the state.
      const comment = comments[bumpActivity.commentUid];
      if (!comment) {
        return undefined;
      }

      // Grab the author from the state.
      const author = findTeammate(comment.authorId, 'conversationCellSelectors makeGetBumpComment');

      if (!author) {
        return undefined;
      }

      const data: BumpCommentModel = {
        comment,
        author,
        // Check whether or not this is the last comment.
        isLastComment: lastComment ? lastComment.uid === comment.uid : true,
      };

      // Return the props.
      return data;
    },
  );
}

/**
 * Selects the last message in a conversation that is not an auto-response.
 *
 * It may fall back to the last message, regardless of auto-response status in the rare case of an
 * old conversation that does not yet have a lastManualMessage in cache.
 */
export function makeGetLastManualMessage<P>(
  getConversation: OptionalConversationSelector<P>,
): OptionalMessageSelector<P> {
  return createSelector([getConversation, getMessages], (conversation, messages) => {
    // The lastMessage fallback covers the rare case in which an old conversation may not have a lastManualMessage in cache.
    const message = conversation && (conversation.lastManualMessage ?? conversation.lastMessage);
    return message && messages[message.uid];
  });
}

// This selector is only needed for mobile until Inconspicuous Auto-responders feature is implement on mobile.
// Desktop/web does not need the lastMessage -- it always uses lastManualMessage instead.
export function makeGetLastMessage<P>(getConversation: ConversationSelector<P>): OptionalMessageSelector<P> {
  return createSelector(
    [getConversation, getMessages],
    (conversation, messages) => conversation.lastMessage && messages[conversation.lastMessage.uid],
  );
}

type DraftSeedsSelector<P> = AppParametricSelector<P, ReadonlyArray<DraftSeedModel>>;

export function makeGetLastDraftSeedAuthors<P>(getDraftSeeds: DraftSeedsSelector<P>) {
  return createSelector([getDraftSeeds, getFindTeammate], (draftSeeds, findTeammate) => {
    const authors = _(draftSeeds)
      .sortBy((s) => -s.updatedAt)
      .uniqBy((s) => s.authorId)
      .map((s) => findTeammate(s.authorId, 'conversationCellSelectors makeGetLastDraftSeedAuthors'))
      .compact()
      .filter((t) => isTeammateActive(t))
      .sortBy((t) => (t.givenName + t.familyName).toLowerCase())
      .value();
    return (authors.length && authors) || undefined;
  });
}

export function makeGetBumpTeammateSource<P>(getBumpActivity: OptionalActivitySelector<P>) {
  return createSelector([getBumpActivity, getFindTeammate], (bumpActivity, findTeammate) => {
    // If we don't have any bump activity, nothing to do.
    if (!bumpActivity) {
      return undefined;
    }

    // If the bump activity isn't a basic activity, nothing to do.
    if (bumpActivity.visualType !== ActivityVisualTypesEnum.BASIC) {
      return undefined;
    }

    // If the source of the activity isn't a teammate, nothing to do.
    if (bumpActivity.source.visualType !== ActivitySourceVisualTypesEnum.TEAMMATE) {
      return undefined;
    }

    // Grab the teammate from the state.
    const teammate = findTeammate(
      bumpActivity.source.teammateId,
      'conversationCellSelectors makeGetBumpTeammateSource',
    );
    if (!teammate) {
      return undefined;
    }

    // Return it.
    return teammate;
  });
}

/*
 * Third line precogs.
 */

export function makeGetUnreadStatus<P>(
  getConversation: ConversationBaseSelector<P>,
  getCellsContext: CellsContextSelector<P>,
): UnreadStatusSelector<P> {
  return createSelector([getConversation, getCellsContext], (conversation, cellsContext) =>
    findUnreadStatus(cellsContext, conversation),
  );
}

export function makeGetCellPrecogs<P>(
  getConversation: ConversationBaseSelector<P>,
  getUnreadStatus: UnreadStatusSelector<P>,
): PrecogsSelector<P> {
  const getPrecogs = makeGetPrecogs(getConversation);

  return createSelector([getPrecogs, getUnreadStatus], (precogs, unreadStatus) => {
    const hasUnread = unreadStatus === UnreadStatusesEnum.MESSAGE_UNREAD;
    return precogs.filter(
      // Don't display sent precogs if we have unread messages.
      // Sent precogs only stop being displayed when the associated activity is received, which does not happen if the conversation is not open.
      (p) => p.status !== PrecogStatusTypesEnum.SENT || !hasUnread,
    );
  });
}

export function makeGetCellCommentingTeammate<P>(getPrecogs: PrecogsSelector<P>) {
  return createSelector(
    [getPrecogs, getFindTeammate],
    (precogs: ReadonlyArray<PrecogModel>, findTeammate) => {
      const precog = precogs.find((p) => p.type === PrecogTypesEnum.COMMENT);
      return (
        precog && findTeammate(precog.teammateId, 'conversationCellSelectors makeGetCellCommentingTeammate')
      );
    },
  );
}

export function makeGetCellReplyingTeammate<P>(getCellPrecogs: PrecogsSelector<P>) {
  return createSelector([getCellPrecogs, getFindTeammate], (precogs, findTeammate) => {
    const precog = precogs.find((p) => p.type === PrecogTypesEnum.MESSAGE);
    return precog && findTeammate(precog.teammateId, 'conversationCellSelectors makeGetCellReplyingTeammate');
  });
}

export function makeGetNumMessages<P>(getConversation: ConversationBaseSelector<P>) {
  return createSelector([getConversation], (conversation) => conversation.numMessages);
}

export function makeGetStatus<P>(
  getCellsContext: CellsContextSelector<P>,
  getConversation: ConversationBaseSelector<P>,
) {
  return createSelector([getCellsContext, getConversation], (cellsContext, conversation) =>
    findConversationStatus(cellsContext, conversation),
  );
}

export function makeGetTrackerStatus<P>(
  getDelegatedTeammateId: TeammateIdSelector<P>,
  getConversation: ConversationBaseSelector<P>,
) {
  return createSelector([getDelegatedTeammateId, getConversation], (delegatedTeammateId, conversation) => {
    // Find the tracker for the delegated teammate.
    const tracker = conversation.trackers[delegatedTeammateId];
    if (!tracker || tracker.stage === ConversationTrackerStagesEnum.NONE) {
      return undefined;
    }

    // Return
    return tracker.status;
  });
}

export function makeGetVisualSearchActivity<P>(
  getSearchActivity: AppParametricSelector<P, SearchActivityModel | undefined>,
): AppParametricSelector<P, VisualSearchActivityModel | undefined> {
  return createSelector([getSearchActivity, getFindTeammate], (searchActivity, findTeammate) => {
    if (searchActivity?.authorId) {
      return {
        ...searchActivity,
        author: findTeammate(searchActivity.authorId, 'makeGetVisualSearchActivity'),
      };
    }

    return searchActivity;
  });
}

/*
 * Selectors for comment draft summary.
 */

export type OptionalDraftSummaryModelSelector<P> = AppParametricSelector<P, DraftSummaryModel | undefined>;

function computeCommentDraftSummaryHasDisplayableContent(summary: DraftSummaryModel): boolean {
  return summary.blurb.trim().length > 0 || summary.hasAttachments;
}

export function makeGetDisplayableCommentDraftSummary<P>(
  getCommentDraftSummary: OptionalDraftSummaryModelSelector<P>,
) {
  return createSelector(getCommentDraftSummary, (commentDraftSummary) =>
    commentDraftSummary && computeCommentDraftSummaryHasDisplayableContent(commentDraftSummary)
      ? commentDraftSummary
      : undefined,
  );
}

export function makeGetCommentDraftSummaryAuthor<P>(
  getCommentDraftSummary: OptionalDraftSummaryModelSelector<P>,
) {
  return createSelector(
    [getCommentDraftSummary, getFindTeammate],
    (summary, findTeammate) =>
      summary?.authorId &&
      findTeammate(summary.authorId, 'conversationCellSelectors makeGetCommentDraftSummaryAuthor'),
  );
}

export function makeGetCommentDraftSummaryWithAuthor<P>(
  getCommentDraftSummary: OptionalDraftSummaryModelSelector<P>,
) {
  return createSelector(
    [getCommentDraftSummary, makeGetCommentDraftSummaryAuthor(getCommentDraftSummary)],
    (summary, author) => {
      if (!summary || !author) {
        return undefined;
      }

      const result: CommentDraftSummary = {summary, author};
      return result;
    },
  );
}
